import { useNeedsYouState } from '../../shell/ShellContext'
import { scopeQueue } from '../../shell/queueScope'
import { Alert, Button, DegradedState } from '../../ds'

/**
 * Knowledge's Inbox banner (v6): "N questions she brought back".
 *
 * Reads the SHELL's queue (`useNeedsYouState`) — the one `useNeedsYou` AppShell runs — so
 * the banner, the bell and the Inbox drawer are one fetch and one number, exactly as Ledger's
 * banners are (MAR-309). It describes QUESTIONS only: a question is the one thing in the
 * queue that is about what she knows. Escalations are Ledger's to raise.
 *
 * The honest states are `scopeQueue`'s, shared with Ledger's waiting banner:
 *  · outside the shell, or nothing read yet (`initialising`) — no banner at all, never an
 *    all-clear;
 *  · degraded — the questions read failed: the count is "at the last check", plus "there
 *    may be more" when it has never loaded; with none known it says the read failed;
 *  · incomplete — still in flight: "so far".
 */

const s = (n: number) => (n === 1 ? '' : 's')

const QUESTIONS = ['questions'] as const

export default function KnowledgeInboxBanner() {
  const shell = useNeedsYouState()
  if (!shell) return null
  const { needsYou: q, openInbox } = shell
  if (q.initialising) return null

  const { total, degraded, unknown, incomplete } = scopeQueue(q, QUESTIONS)

  if (total === 0) {
    if (!degraded) return null
    // A failed read with nothing known to be waiting is NOT an all-clear.
    return (
      <DegradedState title="She couldn’t check for questions she brought back just now — the bell will show them when she can." />
    )
  }

  const noun = `${total} question${s(total)} she brought back`
  const title = degraded
    ? `${noun} at the last check${unknown ? ' — there may be more' : ''}`
    : incomplete
      ? `${noun} so far`
      : noun

  return (
    <Alert
      variant="banner"
      orb
      title={title}
      data-testid="kn-inbox"
      actions={
        <Button variant="gold" size="sm" onClick={openInbox}>
          Open the inbox →
        </Button>
      }
    >
      She told each of them she would find out. Your answer goes back to them, and she keeps it for next time.
    </Alert>
  )
}
